import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'

const NOTIFICATION_STORAGE_KEY = 'esales_pos_notifications'
const MAX_SAVED_NOTIFICATIONS = 50
const MAX_VISIBLE_TOASTS = 3
const DEFAULT_TOAST_DURATION = 4000

export interface Notification {
  id: string
  type: 'success' | 'warning' | 'error' | 'info'
  title: string
  message: string
  timestamp: Date
  duration?: number
  actions?: NotificationAction[]
  read?: boolean
}

interface NotificationAction {
  label: string
  action: () => void
  variant?: 'primary' | 'secondary' | 'danger'
}

interface NotificationContextType {
  notifications: Notification[]  // History for navbar
  activeToasts: Notification[]   // Currently visible toasts
  unreadCount: number
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void
  dismissToast: (id: string) => void
  removeNotification: (id: string) => void
  clearAllNotifications: () => void
  markAllAsRead: () => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

function loadSavedNotifications(): Notification[] {
  try {
    const raw = localStorage.getItem(NOTIFICATION_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as Array<{ id: string; type: Notification['type']; title: string; message: string; timestamp: string; read?: boolean }>
    // Only load notifications from the last 24 hours
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000
    return (parsed || [])
      .filter(n => new Date(n.timestamp).getTime() > oneDayAgo)
      .slice(-MAX_SAVED_NOTIFICATIONS)
      .map(n => ({
        ...n,
        timestamp: new Date(n.timestamp),
        read: n.read ?? false
      }))
  } catch {
    return []
  }
}

function saveNotifications(notifications: Notification[]) {
  try {
    const toSave = notifications.slice(-MAX_SAVED_NOTIFICATIONS).map(n => ({
      id: n.id,
      type: n.type,
      title: n.title,
      message: n.message,
      timestamp: n.timestamp.toISOString(),
      read: n.read
    }))
    localStorage.setItem(NOTIFICATION_STORAGE_KEY, JSON.stringify(toSave))
  } catch {
    // ignore storage errors
  }
}

export const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>(() => loadSavedNotifications())
  const [activeToasts, setActiveToasts] = useState<Notification[]>([])
  const toastTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map())

  // Save to localStorage when notifications change
  useEffect(() => {
    saveNotifications(notifications)
  }, [notifications])

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      toastTimeouts.current.forEach(timeout => clearTimeout(timeout))
    }
  }, [])

  // Dismiss a toast (remove from visible toasts only, keep in history)
  const dismissToast = useCallback((id: string) => {
    setActiveToasts(prev => prev.filter(t => t.id !== id))
    // Clear timeout if exists
    const timeout = toastTimeouts.current.get(id)
    if (timeout) {
      clearTimeout(timeout)
      toastTimeouts.current.delete(id)
    }
  }, [])

  // Remove from history (navbar)
  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
    // Also dismiss toast if visible
    dismissToast(id)
  }, [dismissToast])

  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp'>) => {
    const newNotification: Notification = {
      ...notification,
      id: Math.random().toString(36).substr(2, 9),
      timestamp: new Date(),
      duration: notification.duration ?? DEFAULT_TOAST_DURATION,
      read: false
    }

    // Add to history (for navbar)
    setNotifications(prev => [...prev, newNotification])

    // Add to active toasts (for popup display)
    setActiveToasts(prev => {
      // Limit visible toasts
      const updated = [...prev, newNotification]
      if (updated.length > MAX_VISIBLE_TOASTS) {
        // Remove oldest toasts beyond limit
        const removed = updated.slice(0, updated.length - MAX_VISIBLE_TOASTS)
        removed.forEach(t => {
          const timeout = toastTimeouts.current.get(t.id)
          if (timeout) {
            clearTimeout(timeout)
            toastTimeouts.current.delete(t.id)
          }
        })
        return updated.slice(-MAX_VISIBLE_TOASTS)
      }
      return updated
    })

    // Auto-dismiss toast after duration
    if (newNotification.duration && newNotification.duration > 0) {
      const timeout = setTimeout(() => {
        dismissToast(newNotification.id)
        toastTimeouts.current.delete(newNotification.id)
      }, newNotification.duration)
      toastTimeouts.current.set(newNotification.id, timeout)
    }
  }, [dismissToast])

  const clearAllNotifications = useCallback(() => {
    setNotifications([])
    setActiveToasts([])
    toastTimeouts.current.forEach(timeout => clearTimeout(timeout))
    toastTimeouts.current.clear()
  }, [])

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
  }, [])

  const unreadCount = notifications.filter(n => !n.read).length

  return (
    <NotificationContext.Provider value={{
      notifications,
      activeToasts,
      unreadCount,
      addNotification,
      dismissToast,
      removeNotification,
      clearAllNotifications,
      markAllAsRead
    }}>
      {children}
      <ToastContainer />
    </NotificationContext.Provider>
  )
}

// Toast Container - shows temporary popup notifications
const ToastContainer: React.FC = () => {
  const { activeToasts, dismissToast } = useNotifications()

  const getToastIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success': return '✅'
      case 'warning': return '⚠️'
      case 'error': return '❌'
      case 'info': return 'ℹ️'
      default: return 'ℹ️'
    }
  }

  const getToastStyles = (type: Notification['type']) => {
    switch (type) {
      case 'success': return 'bg-green-500/10 border-green-500/30 shadow-green-500/10'
      case 'warning': return 'bg-yellow-500/10 border-yellow-500/30 shadow-yellow-500/10'
      case 'error': return 'bg-red-500/10 border-red-500/30 shadow-red-500/10'
      case 'info': return 'bg-blue-500/10 border-blue-500/30 shadow-blue-500/10'
      default: return 'bg-blue-500/10 border-blue-500/30 shadow-blue-500/10'
    }
  }

  const getProgressColor = (type: Notification['type']) => {
    switch (type) {
      case 'success': return 'bg-green-500'
      case 'warning': return 'bg-yellow-500'
      case 'error': return 'bg-red-500'
      case 'info': return 'bg-blue-500'
      default: return 'bg-blue-500'
    }
  }

  if (activeToasts.length === 0) return null

  return (
    <div className="fixed top-20 right-4 z-[100] space-y-3 max-w-sm pointer-events-none">
      {activeToasts.map((toast) => (
        <div
          key={toast.id}
          className={`pointer-events-auto backdrop-blur-xl rounded-xl border p-4 shadow-lg ${getToastStyles(toast.type)} animate-in slide-in-from-right fade-in duration-300`}
        >
          <div className="flex items-start gap-3">
            <div className="text-xl flex-shrink-0 mt-0.5">
              {getToastIcon(toast.type)}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-sm text-foreground">{toast.title}</h4>
              <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{toast.message}</p>
              {toast.actions && toast.actions.length > 0 && (
                <div className="flex gap-2 mt-2">
                  {toast.actions.map((action, actionIndex) => (
                    <button
                      key={actionIndex}
                      onClick={() => {
                        action.action()
                        dismissToast(toast.id)
                      }}
                      className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                        action.variant === 'danger' 
                          ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                          : action.variant === 'primary'
                          ? 'bg-primary/20 text-primary hover:bg-primary/30'
                          : 'bg-muted/30 text-muted-foreground hover:bg-muted/50'
                      }`}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => dismissToast(toast.id)}
              className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded hover:bg-muted/30 -mt-1 -mr-1"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Progress bar for auto-dismiss */}
          {toast.duration && toast.duration > 0 && (
            <div className="absolute bottom-0 left-0 right-0 h-1 overflow-hidden rounded-b-xl">
              <div 
                className={`h-full ${getProgressColor(toast.type)} opacity-50`}
                style={{
                  animation: `shrink ${toast.duration}ms linear forwards`
                }}
              />
            </div>
          )}
        </div>
      ))}
      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  )
}

// Hook for easy notification creation
export const useNotificationActions = () => {
  const { addNotification } = useNotifications()

  const showSuccess = useCallback((title: string, message: string, actions?: NotificationAction[], duration?: number) => {
    addNotification({ type: 'success', title, message, actions, duration })
  }, [addNotification])

  const showWarning = useCallback((title: string, message: string, actions?: NotificationAction[], duration?: number) => {
    addNotification({ type: 'warning', title, message, actions, duration })
  }, [addNotification])

  const showError = useCallback((title: string, message: string, actions?: NotificationAction[], duration?: number) => {
    addNotification({ type: 'error', title, message, actions, duration })
  }, [addNotification])

  const showInfo = useCallback((title: string, message: string, actions?: NotificationAction[], duration?: number) => {
    addNotification({ type: 'info', title, message, actions, duration })
  }, [addNotification])

  return {
    showSuccess,
    showWarning,
    showError,
    showInfo
  }
}
